import { useCallback, useEffect, useRef, useState } from "react";
import { useAssistantRuntime } from "@assistant-ui/react";

import { openAttachStream as openAttachStreamEndpoint } from "@/api/chat";
import type { AttachTarget } from "@/pages/frontend/chat/adapters/chat-model-adapter";
import { useAttachRuntime } from "@/pages/frontend/chat/contexts/AttachContext";

export type TurnAttachmentStatusKind =
    | "pending"
    | "active"
    | "horizon"
    | "terminal"
    | "gone";

/**
 * The turn's identity as it was when the attachment became eligible.
 *
 * Frozen by the owning component before either hook runs: resuming a run
 * rebinds the index-keyed row to a fresh empty sibling, so anything read live
 * off that row goes stale the moment the attachment starts.
 */
export interface FrozenTurnIdentity {
    conversationId: number;
    messageId: number;
    originalBranchId: string;
    parentId: string | null;
    threadListItemId: string;
}

/** UX ceiling on waiting for a run that may never end. */
export const QUIESCE_TIMEOUT_MS = 2000;

export type QuiesceResult = { settled: "run" } | { settled: "timeout" };

export interface UseTurnAttachmentOptions {
    /** Null until the owner has frozen one; nothing attaches before then. */
    target: FrozenTurnIdentity | null;
    statusKind: TurnAttachmentStatusKind;
    streamAvailable: boolean;
    /** Monotonic; advances on every successful status read. */
    successfulReadToken: number;
    /** Injected for tests; production watches through the real endpoint. */
    openAttachStream?: AttachTarget["openAttachStream"];
}

/**
 * `resumeRun` is typed `void` but forwards the core's real promise. Anything
 * else means the runtime stopped forwarding it, and the caller must fail toward
 * importing rather than wait on a value that will never settle.
 */
function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
    return (
        typeof (value as { then?: unknown } | null | undefined)?.then ===
        "function"
    );
}

export interface TurnAttachment {
    isAttached: boolean;
    quiesce: () => Promise<QuiesceResult>;
}

/** Watch a turn another connection is driving, for as long as it is worth watching. */
export function useTurnAttachment({
    target,
    statusKind,
    streamAvailable,
    successfulReadToken,
    openAttachStream = openAttachStreamEndpoint,
}: UseTurnAttachmentOptions): TurnAttachment {
    const assistantRuntime = useAssistantRuntime();
    const attach = useAttachRuntime();
    const [isAttached, setIsAttached] = useState(false);

    // A did-happen record, not a liveness flag. It must SURVIVE StrictMode's
    // simulated remount: clearing it in cleanup starts a second run whose
    // predecessor then nulls the shared abort controller, leaving the survivor
    // uncancellable.
    const startedRef = useRef<string | null>(null);
    // Liveness, hook-local on purpose. `thread.isRunning` reads the last message
    // of the SELECTED branch, and restoring the original branch makes it false
    // while the run is still live.
    const liveRef = useRef(false);
    const endedAtTokenRef = useRef<number | null>(null);
    const reattachedRef = useRef(false);
    const horizonHandledRef = useRef(false);
    const tokenRef = useRef(successfulReadToken);
    // THIS run's settlement, not the ambient runEnd event: that event carries no
    // run id, so an unrelated run ending would release the import while this
    // attachment is still writing.
    const runHandleRef = useRef<Promise<unknown> | null>(null);
    const cancelRunRef = useRef<(() => void) | null>(null);
    const attemptRef = useRef(0);
    const effectGenerationRef = useRef(0);
    const viewActiveRef = useRef(false);
    const attachEligibleRef = useRef(false);

    useEffect(() => {
        tokenRef.current = successfulReadToken;
    }, [successfulReadToken]);

    useEffect(() => {
        attachEligibleRef.current = Boolean(
            attach && target && statusKind === "active" && streamAvailable
        );
        if (!attach || !target) return;
        const effectGeneration = ++effectGenerationRef.current;
        viewActiveRef.current = true;
        const targetKey = `${target.conversationId}:${target.messageId}`;
        const thread = assistantRuntime.threads.getById(
            target.threadListItemId
        );
        if (startedRef.current !== null && startedRef.current !== targetKey) {
            if (liveRef.current) cancelRunRef.current?.();
            liveRef.current = false;
            cancelRunRef.current = null;
            runHandleRef.current = null;
            endedAtTokenRef.current = null;
            reattachedRef.current = false;
            horizonHandledRef.current = false;
            startedRef.current = null;
            attemptRef.current += 1;
            setIsAttached(false);
        }

        const cleanup = () => {
            viewActiveRef.current = false;
            queueMicrotask(() => {
                if (effectGenerationRef.current !== effectGeneration) return;
                if (!liveRef.current) return;
                liveRef.current = false;
                cancelRunRef.current?.();
                cancelRunRef.current = null;
            });
        };

        // Repository lookup, not the thread client's message list: the branch
        // being restored is the sibling the resumed run displaced, so it is not
        // in the selected path and the list cannot address it.
        const restoreOriginalBranch = () => {
            thread
                .getMessageById(target.originalBranchId)
                .switchToBranch({ branchId: target.originalBranchId });
        };

        const attachOnce = () => {
            const attempt = ++attemptRef.current;
            let attachEnded = false;
            let settlesViaHandle = false;
            // Arm first: the adapter reads and clears the target on entry to
            // run(), so resuming ahead of this sends the turn to the owner
            // endpoint.
            attach.armAttachTarget({
                assistantMessageId: target.messageId,
                openAttachStream,
                onAttachEnd: () => {
                    if (attemptRef.current !== attempt) return;
                    attachEnded = true;
                    endedAtTokenRef.current = tokenRef.current;
                    if (!settlesViaHandle) liveRef.current = false;
                    cancelRunRef.current = null;
                    if (!viewActiveRef.current) return;
                    setIsAttached(false);
                    // A resumed run rebinds the row rather than unmounting it,
                    // so the recovering affordance only comes back if the
                    // original branch is reselected.
                    restoreOriginalBranch();
                },
            });
            const handle: unknown = thread.resumeRun({
                parentId: target.parentId,
                stream: attach.attachStream,
            });
            const runHandle = isPromiseLike(handle)
                ? Promise.resolve(handle).catch(() => undefined)
                : null;
            settlesViaHandle = runHandle !== null;
            runHandleRef.current = runHandle;
            cancelRunRef.current = () => thread.cancelRun();
            liveRef.current = runHandle !== null || !attachEnded;
            setIsAttached(liveRef.current);
            void runHandle?.then(() => {
                if (
                    attemptRef.current !== attempt ||
                    runHandleRef.current !== runHandle
                )
                    return;
                liveRef.current = false;
                cancelRunRef.current = null;
                if (!viewActiveRef.current) return;
                if (
                    attachEligibleRef.current &&
                    endedAtTokenRef.current !== null &&
                    !reattachedRef.current &&
                    tokenRef.current > endedAtTokenRef.current
                ) {
                    reattachedRef.current = true;
                    attachOnce();
                    return;
                }
                setIsAttached(false);
            });
        };

        if (statusKind === "horizon") {
            if (!liveRef.current || horizonHandledRef.current) return cleanup;
            horizonHandledRef.current = true;
            // Cancellation is the thread runtime's: an abort the adapter sees
            // first returns with no status and the runtime calls that
            // complete/unknown -- a truncated answer shown as finished.
            cancelRunRef.current?.();
            cancelRunRef.current = null;
            setIsAttached(false);
            restoreOriginalBranch();
            return cleanup;
        }

        if (statusKind !== "active" || !streamAvailable) return cleanup;

        if (startedRef.current !== targetKey) {
            startedRef.current = targetKey;
            attachOnce();
            return cleanup;
        }

        // One re-attach, and only on a read that landed after the end. Never a
        // recursive reopen.
        if (
            endedAtTokenRef.current !== null &&
            !reattachedRef.current &&
            successfulReadToken > endedAtTokenRef.current
        ) {
            if (liveRef.current) return cleanup;
            reattachedRef.current = true;
            attachOnce();
        }

        return cleanup;
    }, [
        assistantRuntime,
        attach,
        openAttachStream,
        statusKind,
        streamAvailable,
        successfulReadToken,
        target,
    ]);

    const quiesce = useCallback(async (): Promise<QuiesceResult> => {
        const handle = runHandleRef.current;
        // import() clears and repopulates the repository; it neither cancels
        // nor detaches a run, so the cancel has to land BEFORE the import.
        // Cancelling afterwards re-adds the run's message under the freshly
        // imported head, leaving a row the conversation no longer has.
        if (!liveRef.current || handle === null) return { settled: "run" };

        cancelRunRef.current?.();

        let timer: ReturnType<typeof setTimeout> | undefined;
        const timedOut = new Promise<"timeout">((resolve) => {
            timer = setTimeout(() => resolve("timeout"), QUIESCE_TIMEOUT_MS);
        });
        const outcome = await Promise.race([
            handle.then(() => "run" as const),
            timedOut,
        ]);
        clearTimeout(timer);

        if (outcome === "run") return { settled: "run" };

        // Failing toward the import is deliberate: never importing strands the
        // user on a permanently recovering row, which is worse than a display
        // corruption the repair below undoes.
        console.warn("Attached run did not settle before the import", {
            conversationId: target?.conversationId,
            messageId: target?.messageId,
        });
        return { settled: "timeout" };
    }, [target]);

    return { isAttached, quiesce };
}
